import React, { useEffect, useState } from 'react';
import { ArrowRight, Play } from 'lucide-react';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated Background with Purple-Gold theme */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated gradient orbs - Purple theme */}
        <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-[rgba(124,56,178,0.08)] blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full bg-[rgba(197,155,47,0.08)] blur-[120px] animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(197,155,47,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(197,155,47,0.03)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_60%,transparent_100%)]"></div>
      </div>

      {/* Content */}
      <div className={`relative z-10 max-w-[1200px] mx-auto px-6 text-center transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}>
        {/* Badge */}
        <div className="inline-flex items-center space-x-2 bg-[rgba(197,155,47,0.1)] border border-[rgba(197,155,47,0.3)] rounded-full px-4 py-2 mb-8" style={{ animationDelay: '0.2s' }}>
          <span className="w-2 h-2 bg-[rgb(197,155,47)] rounded-full animate-pulse"></span>
          <span className="text-[rgb(197,155,47)] text-sm font-medium">Empowering Creators Since 2025</span>
        </div>

        {/* Main Heading */}
        <h1 className="text-5xl md:text-7xl font-bold leading-[1.1] mb-6" style={{ animationDelay: '0.3s' }}>
          <span className="text-white">Turn Your Audience</span>
          <br />
          <span className="text-white">Into </span>
          <span className="bg-gradient-to-r from-[rgb(197,155,47)] via-[rgb(224,194,95)] to-[rgb(197,155,47)] text-transparent bg-clip-text animate-shimmer bg-[length:200%_100%]">Consistent Income</span>
        </h1>

        {/* Subheading */}
        <p className="text-xl md:text-2xl text-[rgb(218,218,218)] max-w-[800px] mx-auto mb-12 leading-relaxed" style={{ animationDelay: '0.4s' }}>
          We create and manage digital products for creators — so you can monetize beyond brand deals without lifting a finger.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" style={{ animationDelay: '0.5s' }}>
          <button
            onClick={scrollToContact}
            className="group bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-[rgb(17,17,19)] px-8 py-4 rounded-xl font-semibold text-lg hover:from-[rgb(224,194,95)] hover:to-[rgb(197,155,47)] transition-all duration-300 hover:transform hover:translate-y-[-2px] hover:scale-105 flex items-center space-x-2 shadow-[0_0_30px_rgba(197,155,47,0.4)] hover:shadow-[0_0_50px_rgba(197,155,47,0.6)] w-full sm:w-auto justify-center"
          >
            <span>Start Growing Today</span>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-200" />
          </button>
          <button
            onClick={() => {
              const element = document.getElementById('about');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="group bg-transparent border-2 border-[rgb(124,56,178)] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:border-[rgb(156,80,210)] hover:bg-[rgba(124,56,178,0.1)] transition-all duration-300 hover:transform hover:translate-y-[-2px] flex items-center space-x-2 w-full sm:w-auto justify-center"
          >
            <Play size={20} />
            <span>Learn More</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-20 max-w-[900px] mx-auto" style={{ animationDelay: '0.6s' }}>
          <div className="text-center transform hover:scale-105 transition-transform duration-300">
            <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text mb-2">$0</div>
            <div className="text-[rgb(161,161,170)] text-sm">Upfront Cost</div>
          </div>
          <div className="text-center transform hover:scale-105 transition-transform duration-300">
            <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text mb-2">100%</div>
            <div className="text-[rgb(161,161,170)] text-sm">Your Brand</div>
          </div>
          <div className="text-center transform hover:scale-105 transition-transform duration-300">
            <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text mb-2">3x</div>
            <div className="text-[rgb(161,161,170)] text-sm">Average Revenue Growth</div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .animate-shimmer {
          animation: shimmer 3s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default Hero;